import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Force-change page. Shown whenever `user.must_change_password === true`.
 * After a successful submit the AuthContext refreshes and the user is
 * dropped into /feed as normal.
 */
export default function ChangePassword() {
  const { refresh, logout } = useAuth();
  const nav = useNavigate();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw1.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    if (pw1 !== pw2) {
      setErr("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { new_password: pw1 });
      await refresh();
      nav("/feed", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
      <div className="w-full max-w-md space-y-8">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#d4af37]">Security</p>
          <h1 className="font-serif text-4xl mt-4">Set a new password</h1>
          <p className="text-sm text-white/60 mt-3 leading-relaxed">
            A Concierge has issued you a temporary password. Before you continue,
            please choose a new password — minimum 8 characters.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-6">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/40">New password</label>
            <input
              type="password"
              className="editorial-input mt-2"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              data-testid="change-pw-new"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-white/40">Confirm password</label>
            <input
              type="password"
              className="editorial-input mt-2"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              data-testid="change-pw-confirm"
            />
          </div>

          {err && (
            <p className="text-sm text-red-400" data-testid="change-pw-error">{err}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#d4af37] text-black py-4 uppercase tracking-[0.2em] text-sm font-medium hover:bg-[#f3e5ab] transition disabled:opacity-50"
            data-testid="change-pw-submit"
          >
            {loading ? "Updating…" : "Update password"}
          </button>

          <button
            type="button"
            onClick={async () => {
              await logout();
              nav("/login", { replace: true });
            }}
            className="w-full text-center text-xs text-white/40 hover:text-white/70 uppercase tracking-widest"
            data-testid="change-pw-cancel-logout"
          >
            Cancel and sign out
          </button>
        </form>
      </div>
    </div>
  );
}
